/**
 * DocumentGenerationPanel
 * 
 * Displays a generated document ready for download.
 * Shows as an overlay in the chat when AI generates a PDF/DOCX document.
 */

import React, { useState, useCallback } from 'react';
import {
    Stack,
    Text,
    PrimaryButton,
    DefaultButton,
    Icon,
    Spinner,
    SpinnerSize
} from '@fluentui/react';
import { buildApiUrl } from '../../config/apiConfig';
import styles from './DocumentGenerationPanel.module.css';

export interface DocumentGenerationResult {
    document_url: string;
    document_id: string;
    filename: string;
    format: string;
    content_preview?: string;
}

interface DocumentGenerationPanelProps {
    /** The generated document result */
    result: DocumentGenerationResult;
    /** Callback when user dismisses the panel */
    onDismiss?: () => void;
    /** Whether this is Hebrew/RTL */
    isRtl?: boolean;
}

export const DocumentGenerationPanel: React.FC<DocumentGenerationPanelProps> = ({
    result,
    onDismiss,
    isRtl = false
}) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadComplete, setDownloadComplete] = useState(false);

    const handleDownload = useCallback(async () => {
        if (!result.document_url) return;
        
        setIsDownloading(true);
        
        // Build full URL for cross-origin support (when frontend and backend are on different domains)
        const downloadUrl = result.document_url.startsWith('http') 
            ? result.document_url 
            : buildApiUrl(result.document_url);
        
        // Open download link in new tab (works reliably with blob URLs)
        window.open(downloadUrl, '_blank');
        
        // Small delay then mark complete
        setTimeout(() => {
            setIsDownloading(false);
            setDownloadComplete(true);
        }, 1000);
    }, [result]);

    const getFormatIcon = () => {
        return result.format === 'pdf' ? 'PDF' : 'WordDocument';
    };

    const getFormatLabel = () => {
        return result.format === 'pdf' ? 'PDF' : 'Word';
    };

    return (
        <div className={`${styles.container} ${isRtl ? styles.rtl : ''}`}>
            <div className={styles.panel}>
                {!downloadComplete ? (
                    <>
                        {/* Success header */}
                        <div className={styles.successHeader}>
                            <Icon iconName="CheckMark" className={styles.successCheckIcon} />
                            <Text className={styles.successTitle}>
                                {isRtl ? 'המסמך מוכן!' : 'Document Ready!'}
                            </Text>
                        </div>
                        
                        {/* Document ready card */}
                        <div className={styles.documentReadyCard}>
                            <div className={styles.documentIconWrapper}>
                                <Icon iconName={getFormatIcon()} className={styles.documentReadyIcon} />
                                <div className={styles.formatBadge}>
                                    {getFormatLabel()}
                                </div>
                            </div>
                            <div className={styles.documentInfo}>
                                <Text className={styles.documentName}>
                                    {result.filename}
                                </Text>
                            </div>
                        </div>

                        {/* Download button */}
                        <PrimaryButton
                            iconProps={{ iconName: isDownloading ? undefined : 'Download' }}
                            className={styles.downloadButton}
                            onClick={handleDownload}
                            disabled={isDownloading}
                        >
                            {isDownloading ? (
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                    <Spinner size={SpinnerSize.small} />
                                    <span>{isRtl ? 'מוריד...' : 'Downloading...'}</span>
                                </Stack>
                            ) : (
                                isRtl ? 'הורד מסמך' : 'Download Document'
                            )}
                        </PrimaryButton>
                        
                        {/* Dismiss option */}
                        {onDismiss && (
                            <DefaultButton
                                className={styles.continueButton}
                                text={isRtl ? 'המשך בצ׳אט' : 'Continue Chatting'}
                                onClick={onDismiss}
                            />
                        )}

                        {/* Expiry notice */}
                        <Text className={styles.expiryNotice}>
                            {isRtl ? 'הקישור תקף ל-24 שעות. תוכל לבקש שינויים במסמך.' : 'Link valid for 24 hours. You can ask to modify the document.'}
                        </Text>
                    </>
                ) : (
                    /* Download complete state */
                    <div className={styles.downloadCompleteContainer}>
                        <div className={styles.downloadCompleteIcon}>
                            <Icon iconName="CompletedSolid" />
                        </div>
                        <Text className={styles.downloadCompleteText}>
                            {isRtl ? 'ההורדה החלה!' : 'Download started!'}
                        </Text>
                        <Text className={styles.downloadCompleteSubtext}>
                            {isRtl ? 'בקש שינויים במסמך או המשך בצ׳אט.' : 'Ask to modify or continue chatting.'}
                        </Text>
                        {onDismiss && (
                            <DefaultButton
                                className={styles.continueButton}
                                text={isRtl ? 'המשך בצ׳אט' : 'Continue Chatting'}
                                onClick={onDismiss}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentGenerationPanel;
